import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, X } from "lucide-react";
import LookupSelect from "@/components/images/LookupSelect";
import type { GateMeasurement } from "@shared/api";

interface GateMeasurementFormProps {
  gate: GateMeasurement;
  gateIndex: number;
  onChange: (gate: GateMeasurement) => void;
}

export function GateMeasurementForm({
  gate,
  gateIndex,
  onChange,
}: GateMeasurementFormProps) {
  const { t } = useTranslation();
  const [widths, setWidths] = useState<string[]>(gate.widths.length >= 2 ? gate.widths : ["", ""]);
  const [heights, setHeights] = useState<string[]>(gate.heights.length >= 2 ? gate.heights : ["", ""]);
  const [notes, setNotes] = useState(gate.notes || "");

  useEffect(() => {
    setWidths(gate.widths.length >= 2 ? gate.widths : ["", ""]);
    setHeights(gate.heights.length >= 2 ? gate.heights : ["", ""]);
    setNotes(gate.notes || "");
  }, [gate.id]);

  // Ensure minimum 2 widths and heights
  useEffect(() => {
    if (widths.length < 2) {
      setWidths([...widths, ...Array(2 - widths.length).fill("")]);
    }
    if (heights.length < 2) {
      setHeights([...heights, ...Array(2 - heights.length).fill("")]);
    }
  }, []);

  const updateGate = (updates: Partial<GateMeasurement>) => {
    onChange({ ...gate, ...updates });
  };

  const handleWidthChange = (index: number, value: string) => {
    const newWidths = [...widths];
    newWidths[index] = value;
    setWidths(newWidths);
    updateGate({ widths: newWidths });
  };

  const handleHeightChange = (index: number, value: string) => {
    const newHeights = [...heights];
    newHeights[index] = value;
    setHeights(newHeights);
    updateGate({ heights: newHeights });
  };

  const addWidth = () => {
    setWidths([...widths, ""]);
  };

  const addHeight = () => {
    setHeights([...heights, ""]);
  };

  const removeWidth = (index: number) => {
    if (widths.length > 2) {
      const newWidths = widths.filter((_, i) => i !== index);
      setWidths(newWidths);
      updateGate({ widths: newWidths });
    }
  };

  const removeHeight = (index: number) => {
    if (heights.length > 2) {
      const newHeights = heights.filter((_, i) => i !== index);
      setHeights(newHeights);
      updateGate({ heights: newHeights });
    }
  };

  const handleNotesChange = (value: string) => {
    setNotes(value);
    updateGate({ notes: value });
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{t("measurementExtra.gateFormHeading", { n: gateIndex + 1 })}</h3>
      </div>

      <div className="space-y-3">
        <label className="text-sm font-medium">
          {t("measurementExtra.widthsSectionLabel")}{" "}
          <span className="text-muted-foreground">{t("measurementExtra.dimensionMinTwoHint")}</span>
        </label>
        {widths.map((width, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              type="number"
              inputMode="decimal"
              placeholder={t("measurementExtra.widthPlaceholderMm", { n: index + 1 })}
              value={width}
              onChange={(e) => handleWidthChange(index, e.target.value)}
              className="flex-1"
            />
            {widths.length > 2 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeWidth(index)}
                className="h-10 w-10"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addWidth}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t("measurementExtra.addAnotherWidth")}
        </Button>
      </div>

      <div className="space-y-3">
        <label className="text-sm font-medium">
          {t("measurementExtra.heightsSectionLabel")}{" "}
          <span className="text-muted-foreground">{t("measurementExtra.dimensionMinTwoHint")}</span>
        </label>
        {heights.map((height, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              type="number"
              inputMode="decimal"
              placeholder={t("measurementExtra.heightPlaceholderMm", { n: index + 1 })}
              value={height}
              onChange={(e) => handleHeightChange(index, e.target.value)}
              className="flex-1"
            />
            {heights.length > 2 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeHeight(index)}
                className="h-10 w-10"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addHeight}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t("measurementExtra.addAnotherHeight")}
        </Button>
      </div>

      <div className="space-y-4">
        <LookupSelect
          type="panel_structure"
          value={gate.surface || undefined}
          onValueChange={(value) => updateGate({ surface: value || "" })}
          label={t("gateMeasurementExtra.surfaceLabel")}
          placeholder={t("gateMeasurementExtra.surfacePlaceholder")}
        />
        <LookupSelect
          type="color"
          value={gate.color}
          onValueChange={(value) => updateGate({ color: value })}
          label={t("gateMeasurementExtra.colorLabel")}
          placeholder={t("gateMeasurementExtra.colorPlaceholder")}
        />
        <LookupSelect
          type="prelis"
          value={gate.prelis}
          onValueChange={(value) => updateGate({ prelis: value })}
          label={t("gateMeasurementExtra.prelisLabel")}
          placeholder={t("gateMeasurementExtra.prelisPlaceholder")}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">{t("gateMeasurementExtra.notesLabelSingle")}</label>
        <Textarea
          placeholder={t("gateMeasurementExtra.extraInfoPlaceholder")}
          value={notes}
          onChange={(e) => handleNotesChange(e.target.value)}
          rows={4}
          className="resize-none"
        />
      </div>
    </div>
  );
}
